import { useEffect, useState } from 'react';
import { DateValue } from 'react-aria-components';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';

import { getRentValues } from '@flxlease/flexin-utils';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { RangeValue } from '@react-types/shared';
import isEmpty from 'lodash/isEmpty';
import { useQuery } from 'urql';

import { GET_CALENDAR_EVENTS_BY_PROPERTY } from '../../../../Graphql/queries';
import { formatUsd } from '../../../../Utils/GenericFunctions';
import { PHONE_REGEX } from '../../../../Utils/constants';
import { DateRangePicker } from '../../../components/dateRangePicker/DateRangePicker';
import { BookingFormInputs, IBookingFormProps } from '../types';
import { BookingFooter } from './BookingFooter';

const BookingSection = styled('div')<{ $width: string | undefined }>`
  width: ${({ $width }) => $width || '30%'};
  margin-top: 20px;
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const BookingBox = styled(Box)<{ $variant: 'standard' | 'minimal' }>`
  border: ${({ $variant }) =>
    $variant === 'minimal' ? 'none' : '1px solid rgb(221, 221, 221)'};
  border-radius: 12px;
  box-shadow: ${({ $variant }) =>
    $variant === 'minimal' ? 'none' : 'rgba(0, 0, 0, 0.12) 0px 6px 16px'};
  padding: ${({ $variant }) => ($variant === 'minimal' ? '0' : '24px')};
`;

export const FieldMargin = styled('div')`
  margin-bottom: 15px;
`;

const BookingText = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const Form = styled('form')``;

export const BookingForm = ({
  listing,
  defaultDates,
  onSubmit,
  isSubmitting,
  isSubmitted = false,
  setAmountDue,
  setParentDateRange,
  isBooked = false,
  disabled = false,
  canCancelReservation = false,
  onCancelClick,
  variant = 'standard',
  disableDates = false,
  isCanceling = false,
  width,
}: IBookingFormProps) => {
  const { monthRent, securityDeposit, id: propertyId } = listing;
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<BookingFormInputs>();
  const [dateRange, setDateRange] = useState(null as null | RangeValue<DateValue>);

  const { termLengthDays, totalRent, baseRent } = getRentValues(
    listing.baseRent,
    monthRent,
    dateRange,
  );

  const tomorrow = today(getLocalTimeZone()).add({ days: 1 });
  const grandTotal = totalRent + (securityDeposit || 0);
  const amountDue = baseRent + (securityDeposit || 0);

  useEffect(() => {
    setAmountDue && setAmountDue(amountDue);
  }, [amountDue]);

  const handleRangeChange = (
    newRange: RangeValue<DateValue>,
    field: ControllerRenderProps<BookingFormInputs, 'stayDates'>,
  ) => {
    setDateRange(newRange);
    setParentDateRange && setParentDateRange(newRange);
    field.onChange(newRange);
  };

  const [calendarEventsResult] = useQuery({
    query: GET_CALENDAR_EVENTS_BY_PROPERTY,
    variables: { propertyId: propertyId },
    pause: !propertyId,
  });

  const min30Days = (range: RangeValue<DateValue>) =>
    !range?.start || range?.end.compare(range.start) + 1 < 30
      ? 'Minimum stay length is 30 days.'
      : null;
  const bookedRanges = calendarEventsResult?.data?.getCalendarEventsByProperty.map((event) => [
    parseDate(event.start.split('T')[0]),
    parseDate(event.end.split('T')[0]),
  ]);
  const checkIfDateIsBooked = (date: DateValue) =>
    bookedRanges?.some(
      (interval) => date.compare(interval[0]) >= 0 && date.compare(interval[1]) <= 0,
    ) || false;
  if (!dateRange && defaultDates) {
    const defaultDateRange: RangeValue<DateValue> = {
      start: parseDate(defaultDates.startDate),
      end: parseDate(defaultDates.endDate),
    };
    setDateRange(defaultDateRange);
    setParentDateRange && setParentDateRange(defaultDateRange);
  }

  return (
    <BookingSection $width={width}>
      <BookingBox $variant={variant}>
        {!isBooked && (
          <BookingText>
            <Typography
              variant='h6'
              component='h6'
              fontWeight='bold'
              sx={{ marginBottom: '15px' }}
            >
              {monthRent?.length
                ? `${formatUsd(Math.min(...monthRent))} - ${formatUsd(Math.max(...monthRent))}`
                : formatUsd(baseRent)}
              / month
            </Typography>
          </BookingText>
        )}
        <Form onSubmit={onSubmit && handleSubmit(onSubmit)}>
          <FieldMargin>
            <Controller
              control={control}
              name='stayDates'
              render={({ field }) => (
                <DateRangePicker
                  value={dateRange} // Sync logic with mobile
                  monthRent={monthRent}
                  onChange={(dateRange) => {
                    handleRangeChange(dateRange, field);
                  }}
                  label='Stay Dates'
                  minValue={tomorrow}
                  validate={min30Days}
                  isDateUnavailable={checkIfDateIsBooked}
                  isRequired
                  isDisabled={disableDates}
                />
              )}
            />
          </FieldMargin>
          {!isBooked && (
            <>
              <FieldMargin>
                <TextField
                  fullWidth
                  disabled={disabled}
                  helperText='(123) 456-7890'
                  error={errors.phoneNumber ? true : false}
                  label='Phone Number'
                  type='phone'
                  {...register('phoneNumber', {
                    required: true,
                    pattern: PHONE_REGEX,
                  })}
                />
              </FieldMargin>
              <TextField
                fullWidth
                disabled={disabled}
                error={errors.message ? true : false}
                label='Message'
                multiline
                rows={4}
                {...register('message', {
                  required: true,
                })}
              />
            </>
          )}
          {!isEmpty(sessionStorage.getItem('userId')) ? (
            !isBooked && (
              <Button
                type='submit'
                sx={{ marginTop: '20px' }}
                color='primary'
                variant='contained'
                disabled={isSubmitting || isSubmitted || disabled}
                fullWidth
              >
                {disabled ? 'You have an existing reservation' : 'Book'}
              </Button>
            )
          ) : (
            <Button
              sx={{ marginTop: '20px' }}
              color='primary'
              variant='contained'
              disabled={true}
              fullWidth
            >
              Please log in to book
            </Button>
          )}
        </Form>
        <BookingFooter
          baseRent={baseRent}
          grandTotal={grandTotal}
          securityDeposit={securityDeposit || 0}
          totalRent={totalRent}
          termLength={termLengthDays}
          canCancelReservation={canCancelReservation}
          onCancelClick={onCancelClick}
          isCanceling={isCanceling}
        />
      </BookingBox>
    </BookingSection>
  );
};
